import type { ReactNode } from 'react';
import { wholeShares } from '../lib/scope';

/**
 * THE MANAGER'S CHARTS — CSS bars, no charting library, as the canvas says on
 * `g_charts` in so many words.
 *
 * These live in the role's folder rather than the kit (BRIEF §3): the kit has a
 * `BarRow`, but it draws its own label/track/figure widths and the manager's
 * boards use the stylesheet's own `.bl` / `.bt` / `.bf` / `.bv` with a wider
 * figure column. Restyling the kit to suit one role is how a kit stops being
 * one.
 */

/** `.barrow` — a labelled proportional bar with its figure printed beside it. */
export function Bar({
  label,
  value,
  max,
  display,
  tone = 'brand',
  width = 76,
}: {
  label: ReactNode;
  value: number;
  max: number;
  display?: ReactNode;
  /** The fill. `soft` is the comparison bar the board draws behind the first. */
  tone?: 'brand' | 'soft' | 'warn' | 'danger';
  /** The figure column, in px — the boards use 76 where the kit default is 70. */
  width?: number;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const fill =
    tone === 'soft' ? 'var(--brand-300)'
      : tone === 'warn' ? 'var(--warning-600)'
        : tone === 'danger' ? 'var(--danger-600)'
          : 'var(--brand-500)';
  return (
    <div className="barrow">
      <span className="bl">{label}</span>
      <span className="bt" role="img" aria-label={`${Math.round(pct)}%`}>
        <span className="bf" style={{ display: 'block', width: `${pct}%`, height: '100%', background: fill }} />
      </span>
      <span className="bv" style={{ width }}>{display ?? Math.round(value).toLocaleString('en-US')}</span>
    </div>
  );
}

export interface DayBar {
  key: string;
  value: number;
  label: string;
}

/**
 * The per-day (or per-hour) column chart.
 *
 * The tallest column is brand-500 and the rest brand-300 — the board's way of
 * naming the best day without a legend. A day with nothing in it still draws a
 * stub, because a missing column and a zero column mean different things: the
 * clinic was closed, or the clinic took nothing.
 */
export function DayBars({
  bars,
  height,
  left,
  middle,
  right,
  onPick,
  selected,
}: {
  bars: DayBar[];
  height: number;
  left: ReactNode;
  middle: ReactNode;
  right: ReactNode;
  onPick?: (bar: DayBar) => void;
  selected?: string | null;
}) {
  const max = bars.reduce((m, b) => Math.max(m, b.value), 0);
  const peak = bars.reduce<DayBar | null>((p, b) => (!p || b.value > p.value ? b : p), null);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height, marginTop: 6 }}>
        {bars.map((b) => {
          const pct = max > 0 ? Math.max(4, Math.round((b.value / max) * 100)) : 4;
          const hot = selected ? b.key === selected : b.key === peak?.key && b.value > 0;
          const style = {
            flex: 1,
            background: hot ? 'var(--brand-500)' : 'var(--brand-300)',
            height: `${pct}%`,
            borderRadius: '3px 3px 0 0',
            cursor: onPick ? 'pointer' : undefined,
            padding: 0,
            border: 0,
            minWidth: 0,
          } as const;
          return onPick ? (
            <button key={b.key} type="button" style={style} title={b.label} aria-label={b.label}
              onClick={() => onPick(b)} />
          ) : (
            <div key={b.key} style={style} title={b.label} aria-label={b.label} />
          );
        })}
      </div>
      <div className="flex" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="t-cap mut">{left}</span>
        <span className="t-cap mut">{middle}</span>
        <span className="t-cap mut">{right}</span>
      </div>
    </>
  );
}

export interface Slice {
  key: string;
  label: string;
  value: number;
  colour: string;
}

/**
 * The stacked proportion bar and its inline legend — payment method mix, visit
 * mix. Slices with nothing in them are dropped rather than drawn as a hairline,
 * and the legend prints shares, which is what the bar is showing.
 *
 * THE UNIT IS PER CARD, because the canvas is. `g_charts` prints its VISIT MIX
 * legend as "New 59% · Follow-up 41%" and its PAYMENT METHOD MIX legend as
 * "Cash 61 · Card 17" — the same component, one card with the sign and one
 * without. Dropping the sign everywhere turned the visit legend into two
 * numbers sitting under a card that had just named a different count, so the
 * card that needs it asks for it.
 */
export function StackBar({ slices, sub, percent = false }: {
  slices: Slice[];
  sub?: ReactNode;
  /** Print the share with its `%`, as the board's VISIT MIX legend does. */
  percent?: boolean;
}) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const shown = slices.filter((s) => s.value > 0);
  if (!total) {
    return (
      <>
        <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', marginTop: 6,
          background: 'var(--surface-100)' }} />
        <p className="t-cap mut" style={{ marginTop: 6 }}>Nothing recorded in this period.</p>
        {sub ? <p className="t-cap mut">{sub}</p> : null}
      </>
    );
  }
  const pct = wholeShares(shown.map((s) => s.value));
  return (
    <>
      <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', marginTop: 6 }}>
        {shown.map((s) => (
          <div key={s.key} style={{ width: `${(s.value / total) * 100}%`, background: s.colour }} />
        ))}
      </div>
      <p className="t-cap mut" style={{ marginTop: 6 }}>
        {shown.map((s, i) => (
          <span key={s.key}>
            {i > 0 ? <>&nbsp;·&nbsp;</> : null}
            <span style={{ color: s.colour }}>■</span> {s.label}{' '}
            {pct[i]}{percent ? '%' : ''}
          </span>
        ))}
      </p>
      {sub ? <p className="t-cap mut">{sub}</p> : null}
    </>
  );
}
